import { useState } from 'react';
import { Music } from 'lucide-react';
import { Button, Modal, cx } from '@app/components/ui';
import type { ShareLimitData } from './types';

interface SetlistLimitModalProps {
  limitData: ShareLimitData;
  deletingId: string | null;
  onClose: () => void;
  onReplace: (id: string) => void;
  onDelete: (id: string) => void;
}

const expires = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/** All share-link slots are used: pick one to replace with this setlist, or delete one. */
export const SetlistLimitModal: React.FC<SetlistLimitModalProps> = ({ limitData, deletingId, onClose, onReplace, onDelete }) => {
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <Modal open onClose={onClose} title="Shared setlist limit reached" subtitle={`All ${limitData.maxSlots} share links are in use. Pick one to replace.`} width={460}>
      <div role="radiogroup" aria-label="Shared setlists" className="flex flex-col gap-2 max-h-[50vh] overflow-y-auto">
        {limitData.existingLinks.map((link) => {
          const on = selected === link.id;
          return (
            <button
              key={link.id}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => setSelected(on ? null : link.id)}
              className={cx('flex items-center gap-3 w-full text-left px-3.5 py-3 rounded-[14px] border', on ? 'border-[rgba(208,48,69,.45)] bg-acc-bg' : 'border-line2 bg-fill hover:bg-fill2')}
            >
              <span className={cx('w-9 h-9 rounded-[11px] flex items-center justify-center shrink-0', on ? 'bg-acc text-white' : 'bg-fill2 text-t3')} aria-hidden="true">
                <Music size={15} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-semibold truncate">{link.title || 'Untitled setlist'}</span>
                <span className="block text-[12px] text-t4 mt-0.5">Expires {expires(link.expiresAt)}</span>
              </span>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-2.5 mt-[22px]">
        <Button variant="ghost" shape="form" size="lg" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="danger-ghost"
          shape="form"
          size="lg"
          disabled={!selected || deletingId === selected}
          loading={!!deletingId && deletingId === selected}
          onClick={() => selected && onDelete(selected)}
        >
          Delete
        </Button>
        <Button shape="form" size="lg" className="flex-1" disabled={!selected} onClick={() => selected && onReplace(selected)}>
          Replace selected
        </Button>
      </div>
    </Modal>
  );
};
